import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMouseRestOptions {
  delay?: number;
  enabled?: boolean;
  hasFocus?: boolean;
}

interface MouseRestHandlers {
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
}

export function useMouseRest({
  delay = 3000,
  enabled = true,
  hasFocus = false,
}: UseMouseRestOptions = {}): {
  shouldShowUI: boolean;
  handlers: MouseRestHandlers;
} {
  const [shouldShowUI, setShouldShowUI] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isMouseOverRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();

    if (enabled && !hasFocus && isMouseOverRef.current) {
      timerRef.current = setTimeout(() => {
        setShouldShowUI(false);
      }, delay);
    }
  }, [clearTimer, delay, enabled, hasFocus]);

  const handleMouseMove = useCallback(() => {
    setShouldShowUI(true);
    startTimer();
  }, [startTimer]);

  const handleMouseEnter = useCallback(() => {
    isMouseOverRef.current = true;
    setShouldShowUI(true);
    startTimer();
  }, [startTimer]);

  const handleMouseLeave = useCallback(() => {
    isMouseOverRef.current = false;
    clearTimer();
  }, [clearTimer]);

  // Cleanup on unmount only
  useEffect(() => () => clearTimer(), [clearTimer]);

  // If has focus, always show UI
  const isVisible = hasFocus || shouldShowUI;

  // Update timer when focus changes
  if (hasFocus) {
    clearTimer();
  } else if (isMouseOverRef.current && enabled) {
    // Ensure timer is running when should be
    if (!timerRef.current) {
      startTimer();
    }
  }

  return {
    shouldShowUI: isVisible,
    handlers: {
      onMouseMove: handleMouseMove,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
  };
}
